import React, { ReactNode, useState } from 'react';
import { BringgPopover, PopoverProps, Menu, Tooltip } from '../index';

import './dropdown-menu.scss';

export interface MenuInfo {
	key: React.Key;
	keyPath: React.Key[];
	item: React.ReactInstance;
	domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

export interface DropdownMenuItem {
	key: string | number;
	dataTestId?: string;
	label: ReactNode;
	tooltip?: string;
	onClick?: (info: MenuInfo) => void;
	disabled?: boolean;
	className?: string;
	icon?: ReactNode;
}

interface Props extends Omit<PopoverProps, 'content' | 'popoverType' | 'visible'> {
	items: DropdownMenuItem[];
	menuOptions?: {
		selectable?: boolean;
		defaultSelectedKeys?: string[];
	};
	onMenuSelect?: (key: string | number) => void;
}

const DropdownMenu = ({
	items,
	children,
	onMenuSelect,
	overlayClassName,
	trigger,
	menuOptions,
	...otherProps
}: Props) => {
	const [visible, setVisible] = useState<boolean>(false);

	const menu = (
		<Menu
			onClick={({ key }) => {
				onMenuSelect?.(key);
				setVisible(false);
			}}
			className="bringg-dropdown-menu"
			items={items.map(({ key, label, disabled, onClick, dataTestId, tooltip, className = '', icon = '' }) => ({
				key,
				icon,
				label: (
					<Tooltip title={tooltip} placement="right" zIndex={999999}>
						<span data-test-id={`menu-label-${label}`}>{label}</span>
					</Tooltip>
				),
				disabled,
				onClick: args => {
					const { domEvent } = args;
					domEvent.stopPropagation();

					onClick?.(args);
				},
				'data-test-id': dataTestId || `dropdown-menu-item-${key}`,
				className: `bringg-dropdown-menu-item ${className}`
			}))}
			{...menuOptions}
		/>
	);

	return (
		<BringgPopover
			overlayClassName={`bringg-dropdown-menu-overlay ${overlayClassName || ''}`}
			content={menu}
			visible={visible}
			onVisibleChange={setVisible}
			popoverType="dropdown"
			trigger={trigger || ['hover']}
			noArrow
			{...otherProps}
		>
			{children}
		</BringgPopover>
	);
};

export default DropdownMenu;
